import axios from 'axios'
import BackendLogger from "@/plugins/backend-logger";

/**
 * Services which wraps different axios requests
 */
export default class WrapperService {

  /**
   * Calls axios get request and runs then or catch block
   * if available
   *
   * @param {String} url url to send request to
   * @param {Object} body additional information e.g. params
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async get(url, body, serviceName, successF, errorF) {
    let response;
    try {
      if(body == null) {
        response = await axios.get(url);
      } else if(body.responseType) {
        response = await axios.get(url, {responseType: body.responseType})
      } else {
        response = await axios.get(url, {params:body.params});
      }

      if(successF) {
        successF(response)
      }
    } catch (error) {
      this.errorHandler(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios get request and runs then or catch block
   * if available
   * additionally, passes the error separately to show the specific error cause
   *
   * @param {String} url url to send request to
   * @param {Object} body additional information e.g. params
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async getWithErrorCause(url, body, serviceName, successF, errorF) {
    let response;
    try {
      if(body == null) {
        response = await axios.get(url);
      } else if(body.responseType) {
        response = await axios.get(url, {responseType: body.responseType})
      } else {
        response = await axios.get(url, {params:body.params});
      }

      if(successF) {
        successF(response)
      }
    } catch (error) {
      this.errorHandlerWithClause(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios post request and runs then or catch block
   * if available
   *
   * @param {String} url url to send request to
   * @param {Object} body additional content e.g. a process object
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async post(url, body, serviceName, successF, errorF) {
    try {
      let response = await axios.post(url, body);

      if(successF) {
        successF(response)
      }
    } catch (error) {
      this.errorHandler(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios post request and runs then or catch block if available
   * additionally, passes the error separately to show the specific error cause
   *
   * @param {String} url url to send request to
   * @param {Object} body additional content e.g. a process object
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async postWithErrorCause(url, body, serviceName, successF, errorF) {
    try {
      let response = await axios.post(url, body);

      if(successF) {
        successF(response)
      }
    } catch (error) {
      this.errorHandlerWithClause(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios post request and runs then or catch block
   * Has the ability to monitor the post progress
   *
   * @param {String} url url to send request to
   * @param {Object} body additional content e.g. a process object
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Object} onUploadProgress event object were progress can be read out
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   * @returns {Promise<void>}
   */
  static async monitoringPost(url, body, serviceName, onUploadProgress, successF, errorF) {
    try {
      let response = await axios.post(url, body, {onUploadProgress});

      if(successF) {
        successF(response)
      }
    } catch (error) {
      this.errorHandler(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios put request and runs then or catch block
   * if available
   *
   * @param {String} url url to send request to
   * @param {Object} body additional content e.g. a process object
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async put(url, body, serviceName, successF, errorF) {
    let response;
    try {
      if(body == null) {
        response = await axios.put(url);
      } else {
        response = await axios.put(url, body);
      }

      if(successF) {
        successF(response)
      }
    } catch (error) {
      this.errorHandler(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios put request and runs then or catch block if available
   * additionally, passes the error separately to show the specific error cause
   *
   * @param {String} url url to send request to
   * @param {Object} body additional content e.g. a process object
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async putWithErrorCause(url, body, serviceName, successF, errorF) {
    let response;
    try {
      if(body == null) {
        response = await axios.put(url);
      } else {
        response = await axios.put(url, body);
      }

      if(successF) {
        successF(response)
      }
    } catch (error) {
      this.errorHandlerWithClause(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios delete request and runs then or catch block
   * if available
   *
   * @param {String} url url to send request to
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async delete(url, serviceName, successF, errorF) {
    try {
      let response = await axios.delete(url);

      if(successF) {
        successF(response);
      }
    } catch (error) {
      this.errorHandler(error, serviceName, errorF)
    }
  }

  /**
   * Calls axios delete request and runs then or catch block if available
   * additionally, passes the error separately to show the specific error cause
   *
   * @param {String} url url to send request to
   * @param {String} serviceName name of original service e.g. CountryService
   * @param {Function} successF function which should be called if request is successfull
   * @param {Function} errorF function which should be called if an error is catched
   */
  static async deleteWithErrorCause(url, serviceName, successF, errorF) {
    try {
      let response = await axios.delete(url);

      if(successF) {
        successF(response);
      }
    } catch (error) {
      this.errorHandlerWithClause(error, serviceName, errorF)
    }
  }

  static async errorHandler(error, serviceName, errorF) {
    if(error.name !== 'CanceledError') {
      if(errorF) {
        errorF(serviceName + ": " + error);
      } else {
        BackendLogger.error(serviceName + ": " + error);
      }
    }
  }

  static async errorHandlerWithClause(error, serviceName, errorF){
    if(error.name !== 'CanceledError') {
      if(errorF) {
        errorF(serviceName, error);
      } else {
        BackendLogger.error(serviceName + ": " + error);
      }
    }
  }
}
